import React, { useState, useEffect } from "react";
import SimpleBar from "simplebar-react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Image } from "@themesberg/react-bootstrap";
import { Routes } from "../routes";
import ReactHero from "../assets/img/state-govt-logo.png";
import ProfilePicture from "../assets/img/default_avatar.png";
import { useSelector } from "react-redux";
import { mainTreasuryModules } from "../pages/examples/user-admin/modules";
import { isActivePath } from "../utils/routeMatch";

/* ─── Collapsible menu group ─── */
const CollapsableNavItem = ({
  eventKey, title, icon, children,
  openDropdowns, setOpenDropdowns, onNavClick,
}) => {
  const location = useLocation();
  const isOpen = openDropdowns.includes(eventKey);
  const hasActiveChild = React.Children.toArray(children).some(
    (child) => child && child.props && isActivePath(location.pathname, child.props.link)
  );

  // Auto-expand the group that owns the page you are on
  useEffect(() => {
    if (hasActiveChild) {
      setOpenDropdowns((p) => (p.includes(eventKey) ? p : [...p, eventKey]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () =>
    setOpenDropdowns((p) =>
      p.includes(eventKey) ? p.filter((k) => k !== eventKey) : [eventKey]
    );

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className={`sb-group-header${isOpen ? " is-open" : ""}${
          hasActiveChild ? " has-active-child" : ""
        }`}
      >
        <span className="sb-icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="sb-label">{title}</span>
        <FontAwesomeIcon
          icon={isOpen ? faChevronDown : faChevronRight}
          className="sb-chevron"
        />
      </div>

      {isOpen && (
        <div className="sb-submenu">
          {React.Children.map(children, (child) =>
            child ? React.cloneElement(child, { onNavClick }) : null
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Single nav item ─── */
const NavItem = ({ title, link, icon, onNavClick }) => {
  const location = useLocation();
  const history = useNavigate();
  const isActive = isActivePath(location.pathname, link);

  const go = () => {
    history(link);
    if (onNavClick) onNavClick();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={isActive ? "page" : undefined}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      }}
      className={`sb-item${isActive ? " is-active" : ""}`}
    >
      <span className="sb-icon">
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className="sb-label">{title}</span>
    </div>
  );
};

/* ─── Shared nav list (renders modules) ─── */
const NavList = ({ user, openDropdowns, setOpenDropdowns, onNavClick }) => {
  const toArray = (v) =>
    Array.isArray(v)
      ? v.map((s) => String(s).trim())
      : typeof v === "string"
      ? v.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

  const accessTo = toArray(user?.accessTo);
  const functionalities = toArray(user?.functionalities);
  // Case-insensitive check
  const canAccess = (list, title) =>
    list.some((s) => s.toLowerCase() === title.toLowerCase());

  // The two audit roles own the audit module: its screens are granted by the
  // role itself so an auditor always lands on a usable sidebar, even if the
  // accessTo list has not been backfilled for that account.
  const isAuditor = /^auditor(\s|_)?general$|^auditor$/i.test(
    String(user?.role || "").trim()
  );
  const canOpenModule = (title) =>
    canAccess(accessTo, title) || (isAuditor && title === "Auditor General");

  return (
    <div className="sb-nav">
      {mainTreasuryModules.map((module, index) => {
        if (!canOpenModule(module.title)) return null;
        const modKey = module.title.split(" ").join("-");

        if (module.subMenu && module.subMenu.length) {
          const visibleItems = module.subMenu.filter((item) => {
            const isMDA = item.title === "CC Copies";
            const hasPend = canAccess(functionalities, "Pending Releases");
            return canAccess(functionalities, item.title) || (isMDA && hasPend);
          });
          // If functionalities grants the module but no subMenu items match,
          // show all subMenu items rather than hiding the whole module
          const itemsToRender = visibleItems.length ? visibleItems : module.subMenu;

          return (
            <CollapsableNavItem
              key={index}
              eventKey={modKey}
              title={module.title}
              icon={module.icon}
              openDropdowns={openDropdowns}
              setOpenDropdowns={setOpenDropdowns}
              onNavClick={onNavClick}
            >
              {itemsToRender.map((item, idx) => (
                <NavItem
                  key={`${modKey}-${idx}`}
                  title={item.title}
                  link={item.route}
                  icon={item.icon || module.icon}
                  onNavClick={onNavClick}
                />
              ))}
            </CollapsableNavItem>
          );
        }

        return (
          <NavItem
            key={module.key || index}
            title={module.title}
            link={module.route}
            icon={module.icon}
            onNavClick={onNavClick}
          />
        );
      })}
    </div>
  );
};

/* ─── Main component ─── */
export default () => {
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const history = useNavigate();

  /* Sidebar inner content — shared between desktop and mobile */
  const SidebarContent = ({ onNavClick }) => (
    <>
      <div
        className="sb-brand"
        role="button"
        tabIndex={0}
        onClick={() => history("/")}
      >
        <Image src={ReactHero} className="sb-brand-logo" />
        <div>
          <div className="sb-brand-title">KIFMIS</div>
          <div className="sb-brand-sub">Katsina State</div>
        </div>
      </div>

      <div className="sb-user">
        <Image src={ProfilePicture} className="sb-user-avatar" />
        <div style={{ minWidth: 0 }}>
          <div className="sb-user-name">{user?.fullname || user?.username || "User"}</div>
          <div className="sb-user-role">{user?.role || user?.mda_name || "Staff"}</div>
        </div>
      </div>

      <SimpleBar className="sb-scroll">
        <NavList
          user={user}
          openDropdowns={openDropdowns}
          setOpenDropdowns={setOpenDropdowns}
          onNavClick={onNavClick}
        />
      </SimpleBar>

      <div className="sb-footer">
        <div
          role="button"
          tabIndex={0}
          className="sb-signout"
          onClick={() => {
            if (onNavClick) onNavClick();
            history(Routes.Signin.path);
          }}
        >
          <span className="sb-icon">
            <FontAwesomeIcon icon={faSignOutAlt} />
          </span>
          <span className="sb-label">Sign Out</span>
        </div>
      </div>
    </>
  );

  // Desktop only: the phone header and drawer live in components/mobile/MobileNavbar.
  return (
    <div className="sidebar-v2 d-none d-md-flex" style={{
      position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 1045,
    }}>
      <SidebarContent onNavClick={null} />
    </div>
  );
};
